import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

export const CardSkeleton = ({ count = 1, height = 200 }) => {
  return (
    <>
      {[...Array(count)].map((_, index) => (
        <div key={index} className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700/60 p-6">
          <Skeleton height={20} width="60%" className="mb-4" />
          <Skeleton height={16} width="80%" className="mb-2" />
          <Skeleton height={16} width="70%" className="mb-4" />
          <Skeleton height={height - 100} borderRadius={12} />
        </div>
      ))}
    </>
  );
};

export const TableRowSkeleton = ({ columns = 4, rows = 5 }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            {[...Array(columns)].map((_, index) => (
              <th key={index} className="text-left py-3 px-4">
                <Skeleton height={18} width="80%" borderRadius={8} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...Array(rows)].map((_, rowIndex) => (
            <tr key={rowIndex} className="border-b border-neutral-100 dark:border-neutral-800">
              {[...Array(columns)].map((_, colIndex) => (
                <td key={colIndex} className="py-4 px-4">
                  <Skeleton height={14} borderRadius={8} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const DashboardStatsSkeleton = ({ count = 4 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(count)].map((_, index) => (
        <div key={index} className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700/60 p-6">
          <div className="flex items-center gap-4">
            <Skeleton circle width={40} height={40} />
            <div className="flex-1">
              <Skeleton height={14} width="60%" className="mb-2" borderRadius={8} />
              <Skeleton height={24} width="40%" borderRadius={8} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const ActivityCardSkeleton = ({ count = 3 }) => {
  return (
    <div className="space-y-4">
      {[...Array(count)].map((_, index) => (
        <div key={index} className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700/60 p-5">
          <div className="flex items-start gap-4">
            <Skeleton circle width={44} height={44} />
            <div className="flex-1">
              <Skeleton height={18} width="40%" className="mb-2" borderRadius={8} />
              <Skeleton height={14} width="60%" className="mb-2" borderRadius={8} />
              <Skeleton height={12} width="30%" borderRadius={8} />
            </div>
            <div className="flex gap-2">
              <Skeleton width={32} height={32} borderRadius={10} />
              <Skeleton width={32} height={32} borderRadius={10} />
              <Skeleton width={32} height={32} borderRadius={10} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const SessionListSkeleton = ({ count = 5 }) => {
  return (
    <div className="space-y-4">
      {[...Array(count)].map((_, index) => (
        <div key={index} className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700/60 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1">
              <Skeleton height={22} width="50%" className="mb-2" borderRadius={8} />
              <Skeleton height={14} width="30%" borderRadius={8} />
            </div>
            <Skeleton width={80} height={28} borderRadius={20} />
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Skeleton height={14} width={100} borderRadius={8} />
            <Skeleton height={14} width={120} borderRadius={8} />
            <Skeleton height={14} width={80} borderRadius={8} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const FormSkeleton = () => {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton height={18} width={120} className="mb-2" borderRadius={8} />
        <Skeleton height={44} borderRadius={12} />
      </div>
      <div>
        <Skeleton height={18} width={160} className="mb-2" borderRadius={8} />
        <Skeleton height={44} borderRadius={12} />
      </div>
      <div>
        <Skeleton height={18} width={100} className="mb-2" borderRadius={8} />
        <Skeleton height={120} borderRadius={12} />
      </div>
      <div className="flex gap-3 justify-end">
        <Skeleton width={100} height={40} borderRadius={12} />
        <Skeleton width={120} height={40} borderRadius={12} />
      </div>
    </div>
  );
};

export const ChartSkeleton = ({ height = 300 }) => {
  return (
    <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700/60 p-6">
      <Skeleton height={22} width="40%" className="mb-6" borderRadius={8} />
      <Skeleton height={height} borderRadius={12} />
      <div className="flex justify-center gap-4 mt-4">
        <Skeleton width={80} height={18} borderRadius={8} />
        <Skeleton width={80} height={18} borderRadius={8} />
        <Skeleton width={80} height={18} borderRadius={8} />
      </div>
    </div>
  );
};

export const AvatarSkeleton = ({ size = 40 }) => {
  return <Skeleton circle width={size} height={size} />;
};

export const TextSkeleton = ({ lines = 3, className = '' }) => {
  return (
    <div className={className}>
      {[...Array(lines)].map((_, index) => (
        <Skeleton
          key={index}
          height={14}
          width={index === lines - 1 ? '70%' : '100%'}
          className="mb-2"
          borderRadius={8}
        />
      ))}
    </div>
  );
};

export const PageLoadingSkeleton = () => {
  return (
    <div className="p-6 space-y-8">
      <div>
        <Skeleton height={32} width="40%" className="mb-2" borderRadius={8} />
        <Skeleton height={18} width="60%" borderRadius={8} />
      </div>
      <DashboardStatsSkeleton count={4} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CardSkeleton count={1} height={300} />
        <CardSkeleton count={1} height={300} />
      </div>
      <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700/60 p-6">
        <Skeleton height={22} width="30%" className="mb-4" borderRadius={8} />
        <TableRowSkeleton columns={5} rows={6} />
      </div>
    </div>
  );
};
